/**
 * A tela de entrada.
 *
 * Dois casos que ela precisa acertar e que são fáceis de esquecer:
 *
 * **Um e-mail pode atender a mais de uma organização.** A API responde 409 com a lista, e só
 * então a tela pergunta qual. Pedir a organização antes seria pedir ao usuário que soubesse
 * de um detalhe do nosso modelo de dados para conseguir entrar.
 *
 * **O administrador tem segundo fator** (T-12). Com a senha certa, a API pede o código do
 * aplicativo — ou, na primeira vez, mostra o QR code para cadastrá-lo. A tela troca a senha
 * pelo código e repete o login com os dois: não há token intermediário, e a senha fica só
 * na memória desta tela até o login terminar.
 */
import { useState } from "react";
import type { FormEvent } from "react";

import { ErroDaApi } from "../api/cliente";
import {
  EscolhaDeOrganizacao,
  PedidoDeSegundoFator,
  useSessao,
} from "../api/contexto-sessao";
import type { CadastroDoSegundoFator } from "../api/contexto-sessao";
import { Botao } from "../componentes/base";
import { AssinaturaDoFornecedor, MarcaDoProduto } from "../componentes/marca";

const CLASSE_DO_CAMPO =
  "rounded-botao border border-linha bg-painel-2 px-2.5 py-2 text-tinta";
const CLASSE_DO_ROTULO = "text-rotulo tracking-[0.06em] text-tinta-2 uppercase";

export function Entrada() {
  const { entrar, avisoDeSaida } = useSessao();
  const [email, definirEmail] = useState("");
  const [senha, definirSenha] = useState("");
  const [organizacao, definirOrganizacao] = useState("");
  const [opcoes, definirOpcoes] = useState<{ id: string; nome: string }[]>([]);
  const [erro, definirErro] = useState<string | null>(null);
  const [enviando, definirEnviando] = useState(false);
  // `null`: ainda na senha. Preenchido: a senha conferiu e falta o código.
  const [pedido, definirPedido] = useState<{
    mensagem: string;
    cadastro: CadastroDoSegundoFator | null;
  } | null>(null);
  const [codigo, definirCodigo] = useState("");

  async function submeter(evento: FormEvent) {
    evento.preventDefault();
    definirErro(null);
    definirEnviando(true);
    try {
      await entrar(
        email,
        senha,
        organizacao || undefined,
        pedido ? codigo : undefined,
      );
    } catch (falha) {
      if (falha instanceof PedidoDeSegundoFator) {
        // O cadastro, uma vez mostrado, fica na tela: o QR lido continua valendo.
        definirPedido((atual) => ({
          mensagem: falha.message,
          cadastro: falha.cadastro ?? atual?.cadastro ?? null,
        }));
        definirCodigo("");
      } else if (falha instanceof EscolhaDeOrganizacao) {
        definirOpcoes(falha.organizacoes);
        definirOrganizacao(falha.organizacoes[0]?.id ?? "");
        definirErro(falha.message);
      } else if (falha instanceof ErroDaApi) {
        definirErro(falha.message);
        if (pedido) definirCodigo("");
      } else {
        // Distinguir "o servidor recusou" de "o servidor não respondeu" poupa o suporte de
        // investigar credencial quando o problema é o serviço estar fora.
        definirErro(
          "O cockpit não respondeu. Confira a conexão e tente de novo; se continuar, avise o suporte.",
        );
      }
    } finally {
      definirEnviando(false);
    }
  }

  function voltar() {
    definirPedido(null);
    definirCodigo("");
    definirSenha("");
    definirErro(null);
  }

  return (
    <div className="grid h-full place-items-center overflow-y-auto bg-[radial-gradient(1200px_600px_at_50%_-10%,var(--cor-brilho),var(--cor-fundo))] p-4">
      <form
        onSubmit={(e) => void submeter(e)}
        className="grid w-[min(380px,100%)] gap-3 rounded-[10px] border border-linha bg-painel p-5"
      >
        <h1>
          <MarcaDoProduto destaque />
        </h1>

        {avisoDeSaida && !pedido && (
          <p
            role="status"
            className="rounded-botao border border-linha bg-painel-2 px-2.5 py-2 text-[11.5px] text-tinta-2"
          >
            {avisoDeSaida}
          </p>
        )}

        {pedido ? (
          <SegundoFator
            email={email}
            mensagem={pedido.mensagem}
            cadastro={pedido.cadastro}
            codigo={codigo}
            aoMudarCodigo={definirCodigo}
          />
        ) : (
          <>
            <label className="grid gap-1">
              <span className={CLASSE_DO_ROTULO}>E-mail</span>
              <input
                type="email"
                required
                autoComplete="username"
                value={email}
                onChange={(e) => definirEmail(e.target.value)}
                className={CLASSE_DO_CAMPO}
              />
            </label>

            <label className="grid gap-1">
              <span className={CLASSE_DO_ROTULO}>Senha</span>
              <input
                type="password"
                required
                autoComplete="current-password"
                value={senha}
                onChange={(e) => definirSenha(e.target.value)}
                className={CLASSE_DO_CAMPO}
              />
            </label>
          </>
        )}

        {opcoes.length > 0 && !pedido && (
          <label className="grid gap-1">
            <span className={CLASSE_DO_ROTULO}>Organização</span>
            <select
              value={organizacao}
              onChange={(e) => definirOrganizacao(e.target.value)}
              className={CLASSE_DO_CAMPO}
            >
              {opcoes.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.nome}
                </option>
              ))}
            </select>
          </label>
        )}

        {erro && (
          <p role="alert" className="text-[11.5px] text-vermelho">
            {erro}
          </p>
        )}

        <Botao
          papel="primaria"
          type="submit"
          disabled={enviando || (pedido !== null && codigo.length !== 6)}
        >
          {enviando ? "Entrando…" : pedido ? "Confirmar código" : "Entrar"}
        </Botao>

        {pedido && (
          <Botao onClick={voltar} disabled={enviando}>
            Voltar
          </Botao>
        )}

        {/* A assinatura de quem entrega fica **abaixo** do botão, e não no topo: quem abre
            esta tela vem entrar no cockpit, não conhecer o fornecedor. Mas é a única tela
            que alguém olha parado, e por isso é onde a frase da empresa cabe. */}
        <div className="border-t border-linha pt-2.5">
          <AssinaturaDoFornecedor com_promessa />
        </div>
      </form>
    </div>
  );
}

/**
 * O passo do código. No cadastro, o QR code vem **da API** como SVG — gerado lá para o
 * segredo não passar por biblioteca de terceiros no navegador — e a chave aparece por
 * extenso, para quem não consegue ler o QR (o próprio celular, por exemplo).
 */
function SegundoFator({
  email,
  mensagem,
  cadastro,
  codigo,
  aoMudarCodigo,
}: {
  email: string;
  mensagem: string;
  cadastro: CadastroDoSegundoFator | null;
  codigo: string;
  aoMudarCodigo: (valor: string) => void;
}) {
  return (
    <div className="grid gap-2.5">
      <p className="text-[12px] text-tinta-2">
        <b className="block text-tinta">{email}</b>
        {mensagem}
      </p>

      {cadastro && (
        <div className="grid justify-items-center gap-2 rounded-cartao border border-linha bg-white p-3">
          <img
            alt="QR code para o aplicativo autenticador"
            width={180}
            height={180}
            src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(cadastro.qr_svg)}`}
          />
          <small className="text-center text-[10.5px] break-all text-neutral-700">
            Sem câmera? Digite a chave no aplicativo:
            <br />
            <code className="font-mono text-[11px] text-neutral-900">
              {cadastro.segredo.replace(/(.{4})/g, "$1 ").trim()}
            </code>
          </small>
        </div>
      )}

      <label className="grid gap-1">
        <span className={CLASSE_DO_ROTULO}>Código do aplicativo</span>
        <input
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]{6}"
          maxLength={6}
          required
          autoFocus
          aria-label="Código do aplicativo"
          value={codigo}
          onChange={(e) => aoMudarCodigo(e.target.value.replace(/\D/g, ""))}
          className={`${CLASSE_DO_CAMPO} text-center font-mono text-[18px] tracking-[0.3em]`}
        />
      </label>
    </div>
  );
}
